import React, { useCallback } from "react";
import Select from "../ui-components/Select";
import { useIntl, FormattedMessage } from "react-intl";
import getOptionValue from "../utils/getOptionValue";
import useGetOptionTitle from "../hooks/useGetOptionTitle";
import ServiceConfiguratorQuestionSelectOption from "./ServiceConfiguratorQuestionSelectOption";
import Button from "../ui-components/Button";
import SearchSelect from "@ui-components/SearchSelect";

const ServiceConfiguratorSelectQuestion = ({
  options,
  onChange,
  value,
  multi,
  selectWithButton,
  disabled=false,
  canonicalRender=false,
}) => {
  const intl = useIntl();
  const getOptionTitle = useGetOptionTitle();

  const isActive = useCallback(
    (optionValue) => {
      if (multi) {
        return value && value.indexOf(optionValue) >= 0;
      } else {
        return value && value === optionValue;
      }
    },
    [multi, value]
  );

  const onClickOption = useCallback(
    (active, multi, optionValue) => {
      if (active) {
        if (!multi) {
          return;
        } else {
          onChange(value.filter((v) => v !== optionValue));
        }
      } else {
        if (!multi) {
          onChange(optionValue);
        } else {
          onChange([...(value || []), optionValue]);
        }
      }
    },
    [value, onChange]
  );

  if (!options?.length) {
    return null;
  }

  const hasImages = options.some((o) => o.image_filename);

  if (options.length === 1 && !canonicalRender) {
    return (
      <ServiceConfiguratorQuestionSelectOption option={options[0]} onlyView />
    );
  }

  if (!canonicalRender && (selectWithButton || (!hasImages && multi))) {
    return (
      <div className="flex items-left py-2 mt-3 flex-wrap">
        {options.map((option) => {
          const title = getOptionTitle(option);
          const optionValue = getOptionValue(option);
          const active = isActive(optionValue);

          return (
            <Button
              key={option.id}
              styleType={active ? "default" : "empty"}
              className="py-2 px-4 mr-4 mb-2"
              onClick={() => onClickOption(active, multi, optionValue)}
              disabled={disabled}
            >
              <FormattedMessage id={title} defaultMessage={title} />
            </Button>
          );
        })}
      </div>
    );
  }

  const getSelectOptions = (labelField) => options.map((o) => {
    const title = getOptionTitle(o) || "";
    const optionValue = getOptionValue(o);
    return {
      [labelField]: title
        ? intl.formatMessage({ id: title, defaultMessage: title })
        : "",
      value: optionValue,
    };
  });

  if (canonicalRender && multi) {
    const options = getSelectOptions("label")
    return (
      <SearchSelect
        value={options.filter((o) => value?.indexOf(o.value) >= 0)}
        onChange={(values) => onChange(values.map((v) => v.value))}
        options={options}
        isSearchable={false}
        isClearable={true}
        isMulti
      />
    );
  }

  if (canonicalRender || (!hasImages && !multi)) {
    return (
      <Select
        options={[
          { name: "", value: "" },
          ...getSelectOptions("name"),
        ]}
        value={typeof value === "undefined" ? "" : value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    );
  }

  return (
    <div className="w-full flex flex-wrap">
      {options.map((option) => {
        const optionValue = getOptionValue(option);
        const active = isActive(optionValue);

        return (
          <div
            key={option.id}
            onClick={() => {
              if (!disabled)
                onClickOption(active, multi, optionValue);
            }}
            className={`mr-4 mb-4 ${disabled ? 'cursor-not-allowed opacity-30' : 'cursor-pointer'}`}
          >
            <ServiceConfiguratorQuestionSelectOption
              option={option}
              active={active}
            />
          </div>
        );
      })}
    </div>
  );
};

export default ServiceConfiguratorSelectQuestion;
